import { useState, useRef } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload,
  X,
  Loader2,
  CheckCircle2,
  ImagePlus,
  Building2,
  Ban,
  Phone,
  Eye,
  EyeOff,
  Shield,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, PHONE_HELP, PHONE_HELP_TEL } from "@/lib/api";

const PROPERTY_TYPES = [
  "Single-Family",
  "Multi-Family (2-4)",
  "Multi-Family (5+)",
  "Condo / Townhome",
  "Mobile Home",
  "Land",
  "Commercial",
];

const CONDITIONS = ["Turnkey", "Light cosmetic", "Medium rehab", "Full gut", "Tear down"];
const OCCUPANCY = ["Vacant", "Tenant occupied", "Owner occupied", "Unknown"];

const MAX_PHOTOS = 8;
const MAX_DIM = 1400; // resize longest edge to this
const JPEG_QUALITY = 0.75;

// Resize + compress an image file client-side, return base64 data URL
async function fileToCompressedDataUrl(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width >= height) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        try {
          resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Wholesalers() {
  const [form, setForm] = useState({
    property_address: "",
    city: "",
    state: "",
    zip_code: "",
    property_type: "",
    beds: "",
    baths: "",
    sqft: "",
    year_built: "",
    lot_size: "",
    condition: "",
    occupancy: "",
    arv: "",
    repair_estimate: "",
    asking_price: "",
    monthly_rent: "",
    notes: "",
    wholesaler_name: "",
    wholesaler_phone: "",
    wholesaler_email: "",
    wholesaler_company: "",
  });
  const [photos, setPhotos] = useState([]); // array of {name, dataUrl}
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [processingPhotos, setProcessingPhotos] = useState(false);
  const [feeModalOpen, setFeeModalOpen] = useState(false);
  const [feeAgreement, setFeeAgreement] = useState(""); // "yes" | "no" | ""
  const fileInputRef = useRef(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Max ${MAX_PHOTOS} photos allowed.`);
      return;
    }
    setProcessingPhotos(true);
    try {
      const processed = await Promise.all(
        files.map(async (f) => ({
          name: f.name,
          dataUrl: await fileToCompressedDataUrl(f),
        }))
      );
      setPhotos((prev) => [...prev, ...processed]);
    } catch {
      toast.error("Couldn't process one or more photos. Try smaller files.");
    } finally {
      setProcessingPhotos(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePhoto = (idx) =>
    setPhotos((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_address || !form.wholesaler_name || !form.wholesaler_phone) {
      toast.error("Property address, your name, and phone are required.");
      return;
    }
    // Form is valid — show fee agreement modal before actually submitting
    setFeeAgreement("");
    setFeeModalOpen(true);
  };

  const finalSubmit = async () => {
    if (feeAgreement !== "yes") {
      toast.error("Please select 'Yes' to agree to the broker fee terms.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        photos: photos.map((p) => p.dataUrl),
        source: "wholesalers_section",
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      payload.property_address = form.property_address;
      payload.wholesaler_name = form.wholesaler_name;
      payload.wholesaler_phone = form.wholesaler_phone;
      payload.photos = photos.map((p) => p.dataUrl);
      // Persist the acknowledgement inside the notes so it's auditable
      payload.notes = `${form.notes || ""}\n\n[Broker fee acknowledged: YES — wholesaler agreed to $2,500 fee (reduced proportionally if offer below asking)]`.trim();

      await api.post("/deals", payload);
      setDone(true);
      setFeeModalOpen(false);
      toast.success("Property submitted to website. Broker will review pricing.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try smaller photos?";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="wholesalers"
      data-testid="wholesalers-section"
      className="py-20 md:py-32 bg-white relative overflow-hidden border-t border-[#E5E3DB]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <div className="mb-4">
            <span className="gold-rule" />
            <span className="text-xs uppercase tracking-[0.24em] text-[#2C5545] font-medium">
              For Wholesalers
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0B1B3D] leading-tight mb-5">
            You control the deal. <em className="text-[#C5A059] not-italic font-medium">We bring the buyers.</em>
          </h2>
          <p className="text-[#64748B] leading-relaxed text-base lg:text-lg mb-6">
            This is your deal — you set the asking price, we handle the buyers.
            Every submission is protected by NCND terms and a full audit trail
            so no one can back-door you. You get notified the moment a serious
            buyer shows interest, and the broker handles pricing, marketing,
            and negotiation while you stay in the driver&apos;s seat.
          </p>

          {/* 24/7 help line */}
          <a
            href={`tel:${PHONE_HELP_TEL}`}
            data-testid="wholesaler-help-line"
            className="inline-flex items-center gap-3 px-5 py-3 rounded-lg bg-[#0B1B3D] hover:bg-[#1A2F5C] text-white transition-all shadow-md hover:shadow-lg group"
          >
            <span className="h-9 w-9 rounded-md bg-[#C5A059] text-[#0B1B3D] flex items-center justify-center">
              <Phone className="h-4 w-4" />
            </span>
            <span>
              <span className="block text-[10px] uppercase tracking-[0.22em] text-[#C5A059] leading-none mb-1">
                24/7 Help Line — Call Anytime
              </span>
              <span className="block font-display text-xl leading-none group-hover:text-[#C5A059] transition-colors">
                {PHONE_HELP}
              </span>
            </span>
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* How it works strip */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 space-y-4">
            <div className="rounded-lg border border-[#E5E3DB] bg-[#FAF9F6] p-6">
              <div className="flex items-center gap-2 mb-2">
                <EyeOff className="h-4 w-4 text-[#991B1B]" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#0B1B3D] font-semibold">
                  Price stays private
                </span>
              </div>
              <p className="text-sm text-[#64748B] leading-relaxed mb-2">
                Buyers <strong className="text-[#0B1B3D]">never see</strong> your submitted deal price on this site.
                Pricing lives inside the broker dashboard only — visible to Clear
                Path Home Relief Services and no one else.
              </p>
              <div className="flex items-start gap-2 text-[11px] text-[#64748B] border-t border-[#E5E3DB] pt-3 mt-3">
                <Eye className="h-3 w-3 mt-0.5 text-[#2C5545] shrink-0" />
                <span>
                  <strong className="text-[#0B1B3D]">Internal (broker only):</strong> full pricing, comps, buyer targets.
                </span>
              </div>
              <div className="flex items-start gap-2 text-[11px] text-[#64748B] mt-2">
                <EyeOff className="h-3 w-3 mt-0.5 text-[#991B1B] shrink-0" />
                <span>
                  <strong className="text-[#0B1B3D]">External (buyers / public):</strong> no price shown until the broker sets terms.
                </span>
              </div>
            </div>

            <div className="rounded-lg border border-[#2C5545]/40 bg-[#2C5545]/10 p-5">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-[#2C5545]" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#0B1B3D] font-semibold">
                  Deal Protection Guarantee
                </span>
              </div>
              <p className="text-sm text-[#0F172A] leading-relaxed mb-3">
                Your deal <strong>cannot be stolen</strong>. Every buyer who views
                or contacts a property through this site is <strong>logged, timestamped,
                IP-tracked, and bound to a non-circumvention (NCND) acknowledgement</strong>
                {" "}before they see any details. You&apos;re protected from back-door offers,
                and the broker fee stays intact.
              </p>
              <ul className="space-y-1.5 text-xs text-[#0F172A]">
                <li className="flex gap-2"><Shield className="h-3 w-3 text-[#2C5545] mt-0.5 shrink-0" /> Every buyer inquiry logged in our system</li>
                <li className="flex gap-2"><Shield className="h-3 w-3 text-[#2C5545] mt-0.5 shrink-0" /> IP address + timestamp on every touch</li>
                <li className="flex gap-2"><Shield className="h-3 w-3 text-[#2C5545] mt-0.5 shrink-0" /> NCND agreement required before viewing details</li>
                <li className="flex gap-2"><Shield className="h-3 w-3 text-[#2C5545] mt-0.5 shrink-0" /> Broker acts as sole point of contact — buyers never see wholesaler direct</li>
                <li className="flex gap-2"><Shield className="h-3 w-3 text-[#2C5545] mt-0.5 shrink-0" /> Full audit trail available if a dispute arises</li>
              </ul>
            </div>

            <div className="rounded-lg border border-[#C5A059]/40 bg-[#C5A059]/15 p-5">
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="h-4 w-4 text-[#0B1B3D]" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#0B1B3D] font-semibold">
                  Dispo &amp; broker roles
                </span>
              </div>
              <p className="text-sm text-[#64748B] leading-relaxed">
                Dispo managers can add and edit deal info here. Price is set only
                by the broker (Clear Path Home Relief Services).
              </p>
            </div>

            <a
              href={`tel:${PHONE_HELP_TEL}`}
              data-testid="wholesaler-help-line-sidebar"
              className="block rounded-lg border border-[#0B1B3D] bg-white hover:bg-[#0B1B3D] hover:text-white p-5 transition-colors group"
            >
              <div className="flex items-center gap-2 mb-1 text-[11px] uppercase tracking-[0.2em] text-[#2C5545] group-hover:text-[#C5A059] font-semibold">
                <Phone className="h-3.5 w-3.5" />
                Call Anytime for Help
              </div>
              <div className="font-display text-2xl text-[#0B1B3D] group-hover:text-white leading-tight">
                {PHONE_HELP}
              </div>
              <div className="text-[11px] text-[#64748B] group-hover:text-white/70 mt-1">
                Live broker · deal questions · price checks · buyer matches
              </div>
            </a>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            {done ? (
              <div
                data-testid="deal-form-success"
                className="rounded-lg bg-white border border-[#E5E3DB] p-10 text-center shadow-[0_20px_60px_rgba(11,27,61,0.08)]"
              >
                <CheckCircle2 className="h-12 w-12 text-[#2C5545] mx-auto mb-4" />
                <h3 className="font-display text-3xl text-[#0B1B3D] mb-3">
                  Deal received.
                </h3>
                <p className="text-[#64748B] leading-relaxed mb-6">
                  Our broker will review, set the price, and get back to you
                  within one business day. For urgent deals, text{" "}
                  <span className="font-medium text-[#0B1B3D]">706-506-3860</span>.
                </p>
                <button
                  onClick={() => {
                    setDone(false);
                    setPhotos([]);
                    setForm({
                      property_address: "",
                      city: "",
                      state: "",
                      zip_code: "",
                      property_type: "",
                      beds: "",
                      baths: "",
                      sqft: "",
                      year_built: "",
                      lot_size: "",
                      condition: "",
                      occupancy: "",
                      arv: "",
                      repair_estimate: "",
                      asking_price: "",
                      monthly_rent: "",
                      notes: "",
                      wholesaler_name: "",
                      wholesaler_phone: "",
                      wholesaler_email: "",
                      wholesaler_company: "",
                    });
                  }}
                  data-testid="deal-submit-another"
                  className="px-5 py-2.5 text-sm rounded-md bg-[#0B1B3D] text-white hover:bg-[#1A2F5C]"
                >
                  Submit another deal
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="deal-form"
                className="rounded-lg bg-white border border-[#E5E3DB] p-6 sm:p-8 shadow-[0_20px_60px_rgba(11,27,61,0.06)]"
              >
                <SectionTitle>Property</SectionTitle>

                <div className="grid grid-cols-1 gap-4">
                  <Field label="Street Address *">
                    <Input
                      data-testid="df-address"
                      value={form.property_address}
                      onChange={(e) => upd("property_address", e.target.value)}
                      placeholder="123 Oak St"
                      required
                    />
                  </Field>

                  <div className="grid grid-cols-3 gap-4">
                    <Field label="City">
                      <Input
                        data-testid="df-city"
                        value={form.city}
                        onChange={(e) => upd("city", e.target.value)}
                        placeholder="Athens"
                      />
                    </Field>
                    <Field label="State">
                      <Input
                        data-testid="df-state"
                        value={form.state}
                        onChange={(e) => upd("state", e.target.value)}
                        placeholder="GA"
                      />
                    </Field>
                    <Field label="Zip">
                      <Input
                        data-testid="df-zip"
                        value={form.zip_code}
                        onChange={(e) => upd("zip_code", e.target.value)}
                        placeholder="30606"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Type">
                      <Select
                        value={form.property_type}
                        onValueChange={(v) => upd("property_type", v)}
                      >
                        <SelectTrigger data-testid="df-type" className="h-11">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {PROPERTY_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Condition">
                      <Select
                        value={form.condition}
                        onValueChange={(v) => upd("condition", v)}
                      >
                        <SelectTrigger data-testid="df-condition" className="h-11">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {CONDITIONS.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Occupancy">
                      <Select
                        value={form.occupancy}
                        onValueChange={(v) => upd("occupancy", v)}
                      >
                        <SelectTrigger data-testid="df-occupancy" className="h-11">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {OCCUPANCY.map((o) => (
                            <SelectItem key={o} value={o}>
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                    <Field label="Beds">
                      <Input data-testid="df-beds" value={form.beds} onChange={(e) => upd("beds", e.target.value)} placeholder="3" />
                    </Field>
                    <Field label="Baths">
                      <Input data-testid="df-baths" value={form.baths} onChange={(e) => upd("baths", e.target.value)} placeholder="2" />
                    </Field>
                    <Field label="Sqft">
                      <Input data-testid="df-sqft" value={form.sqft} onChange={(e) => upd("sqft", e.target.value)} placeholder="1,450" />
                    </Field>
                    <Field label="Year">
                      <Input data-testid="df-year" value={form.year_built} onChange={(e) => upd("year_built", e.target.value)} placeholder="1978" />
                    </Field>
                    <Field label="Lot">
                      <Input data-testid="df-lot" value={form.lot_size} onChange={(e) => upd("lot_size", e.target.value)} placeholder="0.25 ac" />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <Field label="Your Asking Price *">
                      <Input
                        data-testid="df-asking-price"
                        value={form.asking_price}
                        onChange={(e) => upd("asking_price", e.target.value)}
                        placeholder="$185,000"
                        className="border-[#C5A059]/60 focus-visible:ring-[#C5A059]"
                      />
                    </Field>
                    <Field label="ARV (estimated)">
                      <Input data-testid="df-arv" value={form.arv} onChange={(e) => upd("arv", e.target.value)} placeholder="$275,000" />
                    </Field>
                    <Field label="Repair Estimate">
                      <Input data-testid="df-repair" value={form.repair_estimate} onChange={(e) => upd("repair_estimate", e.target.value)} placeholder="$35,000" />
                    </Field>
                    <Field label="Monthly Rent">
                      <Input data-testid="df-rent" value={form.monthly_rent} onChange={(e) => upd("monthly_rent", e.target.value)} placeholder="$1,650" />
                    </Field>
                  </div>
                  <p className="text-[11px] text-[#64748B] -mt-2">
                    Your asking price is what you want on your end. The broker adds
                    the $2,500 fee on top when pricing for buyers (kept private from public).
                  </p>

                  <Field label="Notes / Access details">
                    <Textarea
                      data-testid="df-notes"
                      value={form.notes}
                      onChange={(e) => upd("notes", e.target.value)}
                      placeholder="Lockbox code, seller motivation, title issues, tenant status, deadlines, etc."
                      className="min-h-[90px]"
                    />
                  </Field>
                </div>

                {/* Photos */}
                <div className="mt-8">
                  <SectionTitle>Photos (up to {MAX_PHOTOS})</SectionTitle>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {photos.map((p, idx) => (
                      <div
                        key={idx}
                        data-testid={`df-photo-${idx}`}
                        className="relative aspect-square rounded-md overflow-hidden border border-[#E5E3DB] group"
                      >
                        <img src={p.dataUrl} alt={p.name} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removePhoto(idx)}
                          data-testid={`df-photo-remove-${idx}`}
                          className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove photo"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}

                    {photos.length < MAX_PHOTOS && (
                      <label
                        data-testid="df-photo-add"
                        className="aspect-square rounded-md border-2 border-dashed border-[#E5E3DB] hover:border-[#0B1B3D]/40 hover:bg-[#FAF9F6] flex flex-col items-center justify-center gap-2 cursor-pointer text-[#64748B] text-xs"
                      >
                        {processingPhotos ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            <span>Compressing...</span>
                          </>
                        ) : (
                          <>
                            <ImagePlus className="h-6 w-6" />
                            <span>Add photos</span>
                          </>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={onPickFiles}
                          className="hidden"
                          disabled={processingPhotos}
                        />
                      </label>
                    )}
                  </div>
                  <p className="text-[11px] text-[#64748B] mt-2">
                    Photos are resized to 1400px automatically. JPG/PNG/HEIC accepted.
                  </p>
                </div>

                {/* Wholesaler contact */}
                <div className="mt-8">
                  <SectionTitle>Your Contact Info</SectionTitle>

                  <div className="grid grid-cols-1 gap-4">
                    <Field label="Full Name *">
                      <Input
                        data-testid="df-wname"
                        value={form.wholesaler_name}
                        onChange={(e) => upd("wholesaler_name", e.target.value)}
                        placeholder="Alex Carter"
                        required
                      />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field label="Phone *">
                        <Input
                          data-testid="df-wphone"
                          type="tel"
                          value={form.wholesaler_phone}
                          onChange={(e) => upd("wholesaler_phone", e.target.value)}
                          placeholder="(555) 555-0110"
                          required
                        />
                      </Field>
                      <Field label="Email">
                        <Input
                          data-testid="df-wemail"
                          type="email"
                          value={form.wholesaler_email}
                          onChange={(e) => upd("wholesaler_email", e.target.value)}
                          placeholder="alex@company.com"
                        />
                      </Field>
                    </div>
                    <Field label="Company">
                      <Input
                        data-testid="df-wcompany"
                        value={form.wholesaler_company}
                        onChange={(e) => upd("wholesaler_company", e.target.value)}
                        placeholder="Carter Wholesaling LLC"
                      />
                    </Field>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || processingPhotos}
                  data-testid="df-submit"
                  className="mt-8 inline-flex items-center justify-center gap-2 w-full px-8 py-4 rounded-lg bg-[#C5A059] hover:bg-[#B38F4E] disabled:opacity-60 text-[#0B1B3D] font-semibold transition-all shadow-md hover:shadow-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting deal...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Submit Deal for Broker Review
                    </>
                  )}
                </button>

                <p className="text-[11px] text-[#64748B] text-center leading-relaxed mt-3">
                  By submitting, you confirm you have the right to market this
                  property. Pricing is set by the broker after review.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Fee agreement modal — opens on Submit click, gates the actual submission */}
      <Dialog open={feeModalOpen} onOpenChange={setFeeModalOpen}>
        <DialogContent
          data-testid="fee-agreement-modal"
          className="max-w-lg"
          onEscapeKeyDown={(e) => loading && e.preventDefault()}
          onPointerDownOutside={(e) => loading && e.preventDefault()}
        >
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="h-10 w-10 rounded-md bg-[#C5A059] text-[#0B1B3D] flex items-center justify-center font-display text-xl font-bold">
                $
              </div>
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#2C5545] font-semibold">
                Broker fee agreement
              </span>
            </div>
            <DialogTitle className="font-display text-2xl text-[#0B1B3D]">
              One quick thing before we submit
            </DialogTitle>
            <DialogDescription className="text-[#64748B] leading-relaxed pt-2 space-y-3">
              <span className="block">
                A <strong className="text-[#0B1B3D]">$2,500 broker fee</strong> is added on top of every
                deal — that&apos;s how Clear Path Home Relief Services gets paid for
                pricing, marketing, and closing the transaction.
              </span>
              <span className="block">
                If we <strong className="text-[#0B1B3D]">accept an offer lower than asking</strong>, our
                broker fee is <strong className="text-[#0B1B3D]">reduced proportionally</strong> so the
                deal still closes. No surprises at the closing table.
              </span>
              <span className="block rounded-md bg-[#2C5545]/10 border border-[#2C5545]/30 p-3 text-[#0F172A]">
                <strong className="text-[#0B1B3D]">Non-Circumvention (NCND):</strong> Your deal
                is protected. Every buyer who touches this property is logged, IP-tracked,
                and required to sign a non-circumvention acknowledgement before viewing
                details. Buyers cannot contact you directly to back-door the broker fee —
                Clear Path Home Relief Services is the sole point of contact until closing,
                and any attempt to circumvent creates a paper trail actionable under our terms.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2">
            <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
              Do you agree to these terms? *
            </Label>
            <Select value={feeAgreement} onValueChange={setFeeAgreement}>
              <SelectTrigger
                data-testid="fee-agreement-select"
                className="mt-1.5 h-11"
              >
                <SelectValue placeholder="Select an answer to continue" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes" data-testid="fee-agreement-yes">
                  Yes — I agree to the $2,500 fee &amp; the NCND non-circumvention terms
                </SelectItem>
                <SelectItem value="no" data-testid="fee-agreement-no">
                  No — I&apos;d like to cancel
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="mt-4 gap-2 sm:gap-2">
            <button
              type="button"
              onClick={() => setFeeModalOpen(false)}
              disabled={loading}
              data-testid="fee-cancel"
              className="px-5 py-3 rounded-md border border-[#E5E3DB] text-[#0B1B3D] hover:bg-[#F3F2EE] disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={finalSubmit}
              disabled={loading || feeAgreement !== "yes"}
              data-testid="fee-submit-property"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-[#C5A059] hover:bg-[#B38F4E] disabled:opacity-40 disabled:cursor-not-allowed text-[#0B1B3D] font-semibold shadow-md transition-all"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Submit Property to Website
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="mb-4">
      <span className="text-[11px] uppercase tracking-[0.22em] text-[#2C5545] font-semibold">
        {children}
      </span>
      <div className="h-px bg-[#E5E3DB] mt-2" />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
        {label}
      </Label>
      <div className="mt-1.5 [&_input]:h-11">{children}</div>
    </div>
  );
}
